<template>
  <div>
    <div class="">
      <v-card style="">
        <v-card-title class="align-start">
          <v-icon large class="mr-2" color="success">schedule</v-icon>
          <div>
            <div class="headline">Define your cron schedule</div>
            <div class="">Familiarize yourself with the controls, read through the tooltips, and then go over the examples.</div>
            <div>
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <TertiaryBtn color="success" icon="help" class="mx-1 mb-0" small v-bind="attrs" v-on="on">Examples</TertiaryBtn>
                </template>
                <span>Example Contents</span>
              </v-tooltip>

              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <TertiaryBtn color="success" icon="help" class="mx-1 mb-0" small v-bind="attrs" v-on="on">Limitations</TertiaryBtn>
                </template>
                <span>Limitations Contents</span>
              </v-tooltip>
              

              
            </div>
          </div>
        </v-card-title>
        <v-divider />
        <code :class="{'py-2 px-1 title font-weight-regular max-width': true, 'success--text': isValid, 'error--text': !isValid }" v-html="cronExplanation" />
        <v-divider />
        <v-card-text class="pa-0">
          <v-item-group v-model="tab" mandatory>
            <v-item v-for="(item, key, index) in schedule" :key="index" :value="key">
              <div slot-scope="{ toggle }">
                <v-card flat>
                  <div class="d-flex">
                    <v-card-text :style="{ 'min-width': '200px', 'width': getTextWidth(item.value) }" @click="toggle()">
                      <div :class="{ 'error--text': !item.isValid, 'px-2 pb-2': true }">
                        {{ item.label }} {{ item.hint }}
                      </div>
                    </v-card-text>
                    <v-divider vertical />
                    <component :is="item.component" v-model="item.value" @isValid="item.isValid = $event" />
                  </div>
                  <v-divider />
                </v-card>
              </div>
            </v-item>
          </v-item-group>
          <!-- <v-subheader>Cron string (Advanced):</v-subheader> -->
          <v-checkbox color="success" v-model="showCronString" class="ml-3" label="Show cron string (advanced)" />
          <v-layout v-show="showCronString">
            <div v-for="(item, key, index) in schedule" :key="index" :value="key">
              <div :class="{ 'success--text': active && item.isValid, 'error--text': !item.isValid, 'font-weight-medium': active, 'px-2 pb-2': true }">
                {{ item.label }}
              </div>
              <v-text-field @keydown.space.prevent solo flat hide-details :color="item.isValid ? 'success' : 'error'" v-model="item.value" :class="{'error--text': !item.isValid, 'success--text': active && item.isValid, 'px-2 pt-2 clickable display-1 no-max-height no-default-text': true }" style="max-width: 200px;" />
            </div>
          </v-layout>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <PrimaryBtn icon="done" @click.native="onSave" :disabled="!isValid">Save</PrimaryBtn>          
        </v-card-actions>
      </v-card>
    </div>

    <code style="display: block">
      # ┌───────────── minute (0 - 59)
      # │ ┌───────────── hour (0 - 23)
      # │ │ ┌───────────── day of the month (1 - 31)
      # │ │ │ ┌───────────── month (1 - 12)
      # │ │ │ │ ┌───────────── day of the week (0 - 6) (Sunday to Saturday)
      # │ │ │ │ │
      # │ │ │ │ │
      # │ │ │ │ │
      # * * * * *
    </code>
  </div>
</template>

<script>


  import { calculateTextWidth } from '@/helpers/strings'
  // import cronstrue from 'cronstrue'

  export default {
    props: {
      rawCronString: String,
    },
    data: () => ({
      tab: 'minute',
      active: true,
      showCronString: false,      
      schedule: {
        minute: {
          label: 'Minutes',
          hint: '(0-59)',
          value: '0',
          isValid: true,
          component: () => import('@/components/Subcomponents/CronPicker2/Minute'),
        },
        hour: {
          label: 'Hours',
          hint: '(0-23)',
          value: '12',
          isValid: true,
          component: () => import('@/components/Subcomponents/CronPicker2/Hour'),
        },
        dayMonth: {
          label: 'Days of month',
          hint: '(1-31)',
          value: '15,30',
          isValid: true,
          component: () => import('@/components/Subcomponents/CronPicker2/DaysOfMonth'),
        },
        month: {
          label: 'Months',
          hint: '(1-12)',
          value: '*/2',
          isValid: true,
          component: () => import('@/components/Subcomponents/CronPicker2/Month'),
        },
        dayWeek: {
          label: 'Days of week',
          hint: '(1-7)',
          value: '*',
          isValid: true,
          component: () => import('@/components/Subcomponents/CronPicker2/DaysOfWeek'),
        },
      },
    }),
    computed: {      
      isValid() {
        return !Object.values(this.schedule).some(item => !item.isValid)
      },
      cronString: {
        get() {
          return Object.values(this.schedule).map(item => item.value || ' ').join(' ').trim()
        },
        set(val) {
          let maybeValues = val.split(' ')
          this.schedule.minute.value = maybeValues[0]
          this.schedule.hour.value = maybeValues[1]
          this.schedule.dayMonth.value = maybeValues[2]
          this.schedule.month.value = maybeValues[3]
          this.schedule.dayWeek.value = maybeValues[4]
        },
      },
      cronExplanation() {
        try {
          // eslint-disable-next-line no-undef
          return cronstrue.toString(this.cronString, { verbose: true }) + '.'
        } catch (error) {
          console.error('cronexplanation error', error)
          return 'Invalid expression.'
        }
      },
    },
    mounted () {
      this.cronString = this.rawCronString      
    },
    methods: {      
      updateValue(tab, newValue) {
        this.schedule[tab].value = newValue
      },
      getTextWidth(text) {
        let val = calculateTextWidth(text, 'bold 52pt Roboto, sans-serif')
        return val + 'px'
      },
      onSave() {
        var cronStringValue = this.cronString        
        this.$emit('saveCronSetup', cronStringValue)
      },      
    },
  }
</script>